import { useTranslation } from 'react-i18next'
import { Check, Loader2 } from 'lucide-react'

const STEPS = [
  'FETCH_PRODUCT',
  'ANALYZE_IMAGES',
  'KEYWORDS',
  'FIND_COMPETITORS',
  'SCRAPE_COMPETITORS',
  'EVALUATE_COMPETITORS',
  'ANALYZE_LISTING',
  'COMPILE_REPORT',
]

const SECTION_LABELS: Record<string, string> = {
  title: 'Titel',
  bullets: 'Bullets',
  description: 'Beschreibung',
  images: 'Bilder',
  pricing: 'Pricing',
  reviews: 'Bewertungen',
  keyword_matrix: 'Keywords',
  brand_analysis: 'Marke',
  compliance: 'Compliance',
  attributes: 'Attribute',
  product_summary: 'Zusammenfassung',
  strengths: 'Stärken',
  weaknesses: 'Schwächen',
  competitive_benchmark: 'Benchmark',
  conversion_drivers: 'Conversion',
  conversion_blockers: 'Blocker',
  product_improvements: 'Verbesserungen',
  positioning: 'Positionierung',
  optimized_search_terms: 'Suchbegriffe',
  overall_score: 'Gesamtscore',
}

interface AuditProgressStepsProps {
  frontendStep: number
  subStep: string | null
  subCurrent: number
  subTotal: number
}

export function AuditProgressSteps({
  frontendStep,
  subStep,
  subCurrent,
  subTotal,
}: AuditProgressStepsProps) {
  const { t } = useTranslation()
  const s = (key: string) => t(`amazonListing.steps.${key}`)

  return (
    <div className="space-y-1">
      {STEPS.map((step, i) => {
        const stepNum = i + 1
        const state =
          stepNum < frontendStep
            ? 'completed'
            : stepNum === frontendStep
              ? 'active'
              : 'pending'

        return (
          <div key={step}>
            <div className="flex items-center gap-3 py-1">
              <StepIcon state={state} index={i} />
              <span
                className={`text-sm ${
                  state === 'completed'
                    ? 'text-green-600 dark:text-green-400'
                    : state === 'active'
                      ? 'text-orange-600 dark:text-orange-400 font-medium'
                      : 'text-neutral-400 dark:text-neutral-500'
                }`}
              >
                {s(step)}
              </span>
            </div>

            {state === 'active' && (
              <ActiveStepDetail
                step={step}
                subStep={subStep}
                subCurrent={subCurrent}
                subTotal={subTotal}
                s={s}
              />
            )}

            {state === 'completed' && stepNum === frontendStep - 1 && (
              <CompletedStepSub step={step} subTotal={subTotal} s={s} />
            )}
          </div>
        )
      })}
    </div>
  )
}

function StepIcon({ state, index }: { state: string; index: number }) {
  const base = 'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center'
  if (state === 'completed')
    return (
      <div className={`${base} bg-green-500`}>
        <Check className="h-3.5 w-3.5 text-white" />
      </div>
    )
  if (state === 'active')
    return (
      <div className={`${base} bg-orange-500`}>
        <Loader2 className="h-3.5 w-3.5 text-white animate-spin" />
      </div>
    )
  return (
    <div className={`${base} bg-neutral-200 dark:bg-neutral-600`}>
      <span className="text-xs text-neutral-400">{index + 1}</span>
    </div>
  )
}

function ActiveStepDetail({
  step,
  subStep,
  subCurrent,
  subTotal,
  s,
}: {
  step: string
  subStep: string | null
  subCurrent: number
  subTotal: number
  s: (k: string) => string
}) {
  const subText = buildSubText(step, subStep, subCurrent, subTotal, s)
  return (
    <div className="ml-9 mb-2">
      {subText && (
        <p className="text-xs text-orange-500 dark:text-orange-400 mb-1">{subText}</p>
      )}
      <p className="text-xs text-neutral-500 dark:text-neutral-400 italic leading-relaxed">
        {s(`tip_${step}`)}
      </p>
    </div>
  )
}

function CompletedStepSub({
  step,
  subTotal,
  s,
}: {
  step: string
  subTotal: number
  s: (k: string) => string
}) {
  if (step === 'FIND_COMPETITORS' && subTotal > 0) {
    return (
      <p className="ml-9 text-xs text-green-600 dark:text-green-400 mb-1">
        {s('competitorsFound').replace('{{count}}', String(subTotal))}
      </p>
    )
  }
  return null
}

function buildSubText(
  step: string,
  subStep: string | null,
  subCurrent: number,
  subTotal: number,
  s: (k: string) => string,
): string | null {
  if (subTotal <= 0) return null

  if (step === 'SCRAPE_COMPETITORS' || step === 'EVALUATE_COMPETITORS') {
    return s('subProgress')
      .replace('{{current}}', String(subCurrent))
      .replace('{{total}}', String(subTotal))
  }

  if (step === 'ANALYZE_LISTING' && subStep) {
    const label = SECTION_LABELS[subStep] || subStep
    return s('analyzingSection').replace('{{section}}', label)
  }

  return null
}
